// API service for fetching event data from Supabase

import { supabase } from './supabase'

// Map lowercase route types to database type values
const typeMap = {
  'school': 'School',
  'seminar': 'Seminar',
  'workshop': 'Workshop'
}

// Map database type values to lowercase route types
const reverseTypeMap = {
  'School': 'school',
  'Seminar': 'seminar',
  'Workshop': 'workshop'
}

/**
 * Get subscriber count for an event by title
 * @param {string} eventTitle - Event title
 * @returns {Promise<number>} Count of subscribers
 */
const getSubscriberCount = async (eventTitle) => {
  try {
    const { count, error } = await supabase
      .from('subscribers')
      .select('*', { count: 'exact', head: true })
      .eq('title', eventTitle)

    if (error) {
      console.error('Error counting subscribers:', error)
      return 0
    }

    return count || 0
  } catch (error) {
    console.error('Error getting subscriber count:', error)
    return 0
  }
}

/**
 * Fetch all events from Supabase
 * @param {string} type - Event type: 'all', 'school', 'seminar', or 'workshop'
 * @returns {Promise<Array>} Array of events
 */
export const loadEvents = async (type = 'all') => {
  try {
    let query = supabase
      .from('itcpr_events')
      .select('*')
      .eq('published', true) // Only show published events

    // Filter by type if specified
    if (type !== 'all' && typeMap[type]) {
      query = query.eq('type', typeMap[type])
    }

    // Order by date (newest first)
    query = query.order('date', { ascending: false })

    const { data, error } = await query

    if (error) {
      console.error('Error loading events from Supabase:', error)
      throw new Error('NETWORK_ERROR')
    }

    if (!data || data.length === 0) {
      return []
    }

    // Get subscriber counts for all events
    const eventsWithCounts = await Promise.all(
      data.map(async (event) => {
        const subscriberCount = await getSubscriberCount(event.title)
        return {
          ...event,
          // Ensure type is lowercase for routing
          type: reverseTypeMap[event.type] || event.type.toLowerCase(),
          // Ensure status is lowercase for consistency
          status: event.status ? event.status.toLowerCase() : 'upcoming',
          // Use actual subscriber count
          currentEnrollment: subscriberCount
        }
      })
    )

    return eventsWithCounts
  } catch (error) {
    console.error('Error loading events:', error)
    // Re-throw with specific error type if it's already set
    if (error.message === 'NETWORK_ERROR' || error.message === 'NOT_FOUND') {
      throw error
    }
    throw new Error('NETWORK_ERROR')
  }
}

/**
 * Fetch a single event by ID from Supabase
 * @param {string} eventId - Event ID
 * @param {string} eventType - Event type (optional, for validation)
 * @returns {Promise<Object>} Event data
 */
export const fetchEventData = async (eventId, eventType = null) => {
  try {
    let query = supabase
      .from('itcpr_events')
      .select('*')
      .eq('id', eventId)
      .eq('published', true) // Only fetch published events

    const { data, error } = await query.single()

    if (error) {
      console.error('Error fetching event from Supabase:', error)
      
      // Check if it's a not found error
      if (error.code === 'PGRST116' || error.message?.includes('No rows')) {
        throw new Error('NOT_FOUND')
      }
      
      throw new Error('NETWORK_ERROR')
    }

    if (!data) {
      throw new Error('NOT_FOUND')
    }

    // Validate required fields
    const requiredFields = ['id', 'title', 'type', 'status']
    const missingFields = requiredFields.filter(field => !data[field])

    if (missingFields.length > 0) {
      throw new Error('INVALID_DATA')
    }

    // Optional: Validate event type matches if provided
    if (eventType && typeMap[eventType]) {
      const dbType = typeMap[eventType]
      if (data.type !== dbType) {
        console.warn(`Event type mismatch: expected ${dbType}, got ${data.type}`)
      }
    }

    // Get subscriber count for this event
    const subscriberCount = await getSubscriberCount(data.title)

    // Normalize the data format
    return {
      ...data,
      // Ensure type is lowercase for routing
      type: reverseTypeMap[data.type] || data.type.toLowerCase(),
      // Ensure status is lowercase for consistency
      status: data.status ? data.status.toLowerCase() : 'upcoming',
      // Use actual subscriber count
      currentEnrollment: subscriberCount
    }
  } catch (error) {
    console.error('Error fetching event data:', error)
    // Re-throw with specific error type
    if (error.message === 'NOT_FOUND' || error.message === 'NETWORK_ERROR' || error.message === 'INVALID_DATA') {
      throw error
    }
    // Network or other errors
    throw new Error('NETWORK_ERROR')
  }
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use loadEvents instead
 */
export const fetchEvents = async () => {
  console.warn('fetchEvents() is deprecated. Use loadEvents() instead.')
  const events = await loadEvents('all')
  
  // Return in the old format for backward compatibility
  return {
    schools: events.filter(e => e.type === 'school').map(e => e.id),
    seminars: events.filter(e => e.type === 'seminar').map(e => e.id),
    workshops: events.filter(e => e.type === 'workshop').map(e => e.id)
  }
}

